import { BottomAction } from '@euler/app/components/BottomAction';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { useServiceSubjectsHost } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { SegmentControl } from '@euler/components/SegmentControl';
import { Input } from '@euler/components/form/Input';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { FormController, useFormController } from '@euler/lib/form/controller';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { FaultLikeServiceSubject } from '@euler/model';
import { ServiceSubjectType } from '@euler/model/enum';
import { showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { makeUniqueIdAlphabetic } from '@euler/utils/id';
import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import {
  ReactNode,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { serviceSubjectItemToSubject } from '../functions/serviceSubjectItemToSubject';
import {
  ConstructionServiceSubjectCategory,
  ServiceSubjectCategory,
} from '../types';
import { useServiceSubjectsContext } from './ServiceSubjectsContext';

type Data = {
  type: ServiceSubjectType;
  title: string;
  categoryId: string;
  note?: string;
};

type Ref = {
  controller: FormController<Data>;
};

const kServiceSubjectTypes: ServiceSubjectType[] = [
  ServiceSubjectType.Construction,
  ServiceSubjectType.Fault,
  ServiceSubjectType.Other,
];
const kServiceSubjectTypeSegments = ['施工项目', '检修项目', '其他项目'];

const isConstructionServiceSubjectCategory = (
  category: ServiceSubjectCategory,
): category is ConstructionServiceSubjectCategory => {
  return category.type === 'construction';
};

export const CustomServiceSubjectBottomSheetModal = memo(
  ({
    visible,
    itemBeingEdited,
    onDismiss,
  }: {
    visible?: boolean;
    itemBeingEdited?: FaultLikeServiceSubject;
    onDismiss?: () => void;
  }) => {
    useToggleAutomaticKeyboard(true);

    const { rpx } = useResponsiveContext();
    const { navBarHeight, safeAreaInsets: insets } = useSystemMetrics();
    const { orderDetail$ } = useServiceSubjectsHost();
    const { categories, addServiceSubject, updateServiceSubject } =
      useServiceSubjectsContext();

    const ref = useRef<Ref>(null);

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show: visible,
        topMargin: navBarHeight,
        minSnapPoint: '50%',
        mediumSnapPoint: '75%',
        index: 1,
        enableDismissOnClose: true,
        enablePanDownToClose: true,
        dismissWhenPressBackdrop: true,
        onDismiss,
      });
    const animatedPosition = useSharedValue(0);
    const height = useGeneralBottomSheetHeight();
    const style = useAnimatedStyle(() => {
      return {
        opacity: interpolate(
          animatedPosition.value,
          [(height * 2) / 4, height],
          [1, 0],
        ),
      };
    });

    const onCancel = usePersistFn(() => {
      bottomSheetModalRef.current?.dismiss();
    });

    const onDone = usePersistFn(async () => {
      const controller = ref.current?.controller;
      if (!controller) return;

      const valid = await controller.validateAll();
      if (!valid) return;

      const name = controller.form.title.value.trim();
      const type = controller.form.type.value;
      const note = controller.form.note.value?.trim();

      if (
        (itemBeingEdited == null || name !== itemBeingEdited.name) &&
        orderDetail$.value.serviceSubjects?.some(
          x => x.type === type && x.name === name,
        )
      ) {
        void showAlert('提示', `"${name}"已经在服务项目列表中!`);
        return;
      }

      if (itemBeingEdited != null) {
        // update the existing service subject.
        updateServiceSubject(itemBeingEdited, { name, note });
        bottomSheetModalRef.current?.dismiss();
        return;
      }

      if (orderDetail$.value.serviceSubjects?.some(x => x.name === name)) {
        void showAlert('提示', `"${name}"已经在服务项目列表中!`);
        return;
      }

      if (type === ServiceSubjectType.Construction) {
        const categoryList = categories.filter(x => x.type === 'construction');
        // const categoryId = controller.form.categoryId.value;
        const workItems = categoryList.flatMap(x =>
          x.groups.flatMap(g => g.items),
        );
        const existing = workItems.find(x => x.title === name);
        if (existing) {
          const subject = serviceSubjectItemToSubject(existing);
          addServiceSubject(subject);
        } else {
          addServiceSubject({
            type: ServiceSubjectType.Construction,
            name,
          });
        }
      } else if (type === ServiceSubjectType.Fault) {
        addServiceSubject({
          id: makeUniqueIdAlphabetic(),
          type,
          name,
          note,
        });
      } else if (type === ServiceSubjectType.Other) {
        addServiceSubject({
          id: makeUniqueIdAlphabetic(),
          type,
          name,
          note,
        });
      }

      bottomSheetModalRef.current?.dismiss();
    });

    const Footer = usePersistFn((props: BottomSheetFooterProps) => {
      return (
        <BottomSheetFooter {...props}>
          <BottomAction containerStyle={style}>
            <BottomAction.Button
              text="返回"
              variant="default"
              onPress={onCancel}
            />
            <BottomAction.Button
              text={itemBeingEdited ? '确定' : '添加'}
              variant="primary"
              css={`
                margin-left: ${rpx(15)}px;
              `}
              onPress={onDone}
            />
          </BottomAction>
        </BottomSheetFooter>
      );
    });

    const Header = useCallback(
      () => (
        <Center
          css={`
            padding: ${rpx(10)}px 0;
            border-bottom-width: 1px;
            border-bottom-color: #f1f1f1;
            height: ${rpx(44)}px;
          `}
        >
          <Typography.Label size={rpx(16)} color="#333" bold>
            {itemBeingEdited != null
              ? '编辑自定义服务项目'
              : '添加自定义服务项目'}
          </Typography.Label>
        </Center>
      ),
      [itemBeingEdited, rpx],
    );

    // Fix: BottomSheet specific scroll view components do not work on Web right now.
    // https://github.com/gorhom/react-native-bottom-sheet/issues/11
    const ScrollComponent =
      Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

    return (
      <BottomSheetModal
        {...bottomSheetProps}
        footerComponent={Footer}
        animatedPosition={animatedPosition}
        keyboardBehavior="interactive"
        keyboardBlurBehavior="none"
        ref={bottomSheetModalRef}
      >
        <ScrollComponent
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(15),
          }}
        >
          <Header />
          <Content
            categories={categories}
            itemBeingEdited={itemBeingEdited}
            ref={ref}
          />
        </ScrollComponent>
      </BottomSheetModal>
    );
  },
);

const Content = memo(
  forwardRef<
    Ref,
    {
      categories: ServiceSubjectCategory[];
      itemBeingEdited?: FaultLikeServiceSubject;
    }
  >(({ categories, itemBeingEdited }, ref) => {
    const { rpx } = useResponsiveContext();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    const categoryList = categories.filter(
      isConstructionServiceSubjectCategory,
    );

    const controller = useFormController<Data>({
      type: {
        required: true,
        defaultValue: itemBeingEdited?.type ?? ServiceSubjectType.Construction,
      },
      title: {
        required: true,
        defaultValue: itemBeingEdited?.name ?? '',
      },
      categoryId: {
        required: false,
        defaultValue: categoryList[0]?.categoryId,
      },
      note: {
        required: false,
        defaultValue: itemBeingEdited?.note ?? '',
      },
    });

    useImperativeHandle(ref, () => ({
      controller,
    }));

    const { form } = controller;

    const onSubjectTypeIndexChange = useCallback(
      (index: number) => {
        form.type.update(kServiceSubjectTypes[index]);
      },
      [form.type],
    );

    return (
      <View
        css={`
          padding-bottom: ${rpx(100)}px;
        `}
      >
        {itemBeingEdited == null && (
          <Section title="服务项目类型">
            <SegmentControl
              segments={kServiceSubjectTypeSegments}
              selectedIndex={kServiceSubjectTypes.indexOf(form.type.value)}
              onSelectedIndexChange={onSubjectTypeIndexChange}
              textStyle={{ fontFamily: FontFamily.NotoSans.Medium }}
              activeTextStyle={{ fontFamily: FontFamily.NotoSans.Bold }}
            />
          </Section>
        )}
        <Section title="服务项目名称" required={true}>
          <Input
            placeholder="输入服务项目名称"
            keyboardType="default"
            editable={true}
            value={form.title.value}
            errorMsg={form.title.error}
            clearButtonMode="always"
            returnKeyType="done"
            onBlur={form.title.onBlur}
            onChangeText={form.title.update}
            errorBottomOffset={rpx(4)}
            css={`
              padding-top: ${rpx(8)}px;
              padding-bottom: ${rpx(8)}px;
              height: ${rpx(48)}px;
            `}
          />
        </Section>
        {form.type.value === ServiceSubjectType.Construction &&
          itemBeingEdited == null && (
            <Section title="施工项目类型">
              {categoryList.map((category, i) => (
                <TouchableOpacity
                  key={category.categoryId}
                  css={`
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: ${i === 0 ? rpx(10) : 0}px;
                    border-top-width: 1px;
                    border-top-color: ${i === 0 ? '#eee' : 'transparent'};
                    border-bottom-width: 1px;
                    border-bottom-color: ${i === categories.length - 1
                      ? '#eee'
                      : '#eee'};
                  `}
                  onPress={() => form.categoryId.update(category.categoryId)}
                >
                  <View
                    css={`
                      flex: 1;
                      margin-right: ${rpx(10)}px;
                      padding: ${rpx(5)}px 0;
                    `}
                  >
                    <Typography.Label regular size={rpx(16)} color="#555">
                      {category.name}
                    </Typography.Label>
                    <Typography.Label light size={rpx(12)} color={'#888'}>
                      {category.template?.procedures
                        .map(x => x.name)
                        .join('、') ?? ''}
                    </Typography.Label>
                  </View>
                  <Icon
                    name="check"
                    size={rpx(24)}
                    color={
                      category.categoryId === form.categoryId.value
                        ? '#000'
                        : 'transparent'
                    }
                  />
                </TouchableOpacity>
              ))}
            </Section>
          )}
        {form.type.value === ServiceSubjectType.Fault ||
        form.type.value === ServiceSubjectType.Other ? (
          <Section title="相关客户备注">
            <Input
              placeholder="输入客户备注"
              keyboardType="default"
              multiline={true}
              numberOfLines={3}
              value={form.note.value}
              errorMsg={form.note.error}
              clearButtonMode="always"
              returnKeyType="done"
              onBlur={form.note.onBlur}
              onChangeText={form.note.update}
              errorBottomOffset={rpx(4)}
              withVoiceInput={isVoiceInputEnabled}
              css={`
                padding-top: ${rpx(8)}px;
                padding-bottom: ${rpx(8)}px;
              `}
            />
          </Section>
        ) : null}
      </View>
    );
  }),
);

const Section = memo(
  ({
    title,
    style,
    required,
    children,
  }: {
    title: string;
    children?: ReactNode;
    required?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          margin: ${rpx(15)}px;
        `}
        style={style}
      >
        <Typography.Text medium color="#333" size={rpx(15)}>
          {title}
          {required && <Text style={{ color: 'red' }}> *</Text>}
        </Typography.Text>
        <View
          css={`
            margin-top: ${rpx(5)}px;
          `}
        >
          {children}
        </View>
      </View>
    );
  },
);
